import React, { useState, useEffect } from 'react';
import { Row, Col, Form, Button, Image } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '../../shared/api';
import PageShell from '../../components/layout/PageShell';
import PageSection from '../../components/layout/PageSection';
import AppButton from '../../components/ui/AppButton';
import { CenteredLoader } from '../../components/LoadingSpinner';

const CompanySettings = () => {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const [saving, setSaving] = useState(false);
  const [company, setCompany] = useState(null);
  const [formData, setFormData] = useState({
    company_name: '',
    company_logo: null,
    company_clr: '#ffffff',
    company_id: '',
    tax_id: '',
    resp_person: '',
    street: '',
    city: '',
    postal_code: '',
    telephone: '',
    mobile: '',
    email: '',
    description: ''
  });
  const [logoPreview, setLogoPreview] = useState(null);
  const [logoFile, setLogoFile] = useState(null);
  const [removeLogo, setRemoveLogo] = useState(false);
  const [logoDarkPreview, setLogoDarkPreview] = useState(null);
  const [logoDarkFile, setLogoDarkFile] = useState(null);
  const [removeLogoDark, setRemoveLogoDark] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);

  const [aiSettings, setAiSettings] = useState({ id: null, use_local_ai: true, openai_api_key: '', openai_api_key_set: false, openai_model: 'gpt-4o-mini' });

  const { isLoading: loading, data: companyQueryData } = useQuery({
    queryKey: ['company-settings'],
    queryFn: async () => {
      const listResponse = await api.get('/companies/api/companies/');
      if (Array.isArray(listResponse) && listResponse.length > 0) {
        const companyData = await api.get(`/companies/api/companies/${listResponse[0].id}/`);
        return companyData;
      }
      return null;
    },
  });

  useEffect(() => {
    if (!companyQueryData) return;
    setCompany(companyQueryData);
    setFormData({
      company_name: companyQueryData.company_name || '',
      company_logo: companyQueryData.company_logo || null,
      company_clr: companyQueryData.company_clr || '#ffffff',
      company_id: companyQueryData.company_id || '',
      tax_id: companyQueryData.tax_id || '',
      resp_person: companyQueryData.resp_person || '',
      street: companyQueryData.street || '',
      city: companyQueryData.city || '',
      postal_code: companyQueryData.postal_code || '',
      telephone: companyQueryData.telephone || '',
      mobile: companyQueryData.mobile || '',
      email: companyQueryData.email || '',
      description: companyQueryData.description || ''
    });
    setLogoPreview(companyQueryData.company_logo);
    setLogoDarkPreview(companyQueryData.company_logo_dark || null);
    setRemoveLogo(false);
    setRemoveLogoDark(false);
  }, [companyQueryData]);

  useQuery({
    queryKey: ['company-ai-settings'],
    queryFn: async () => {
      const response = await api.get('/settings/api/company/');
      const data = Array.isArray(response) ? response[0] : response;
      if (data) {
        setAiSettings({
          id: data.id,
          use_local_ai: data.use_local_ai ?? true,
          openai_api_key: '',
          openai_api_key_set: !!data.openai_api_key_set,
          openai_model: data.openai_model || 'gpt-4o-mini',
        });
      }
      return data;
    },
  });

  const invalidateAi = () => queryClient.invalidateQueries({ queryKey: ['company-ai-settings'] });

  const aiSaveMutation = useMutation({
    mutationFn: (payload) => aiSettings.id
      ? api.patch(`/settings/api/company/${aiSettings.id}/`, payload)
      : api.post('/settings/api/company/', payload),
    onSuccess: () => { setSuccess(t('settings.company.aiSettings.saved')); invalidateAi(); },
    onError: (err) => setError(err.message || t('settings.company.aiSettings.saveFailed')),
  });
  const aiSaving = aiSaveMutation.isPending || false;

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleAiChange = (e) => {
    const { name, value, type, checked } = e.target;
    setAiSettings(prev => ({ ...prev, [name]: type === 'checkbox' ? checked : value }));
  };

  const handleAiSave = () => {
    const payload = {
      use_local_ai: aiSettings.use_local_ai,
      openai_model: aiSettings.openai_model,
      ...(aiSettings.openai_api_key.trim() ? { openai_api_key: aiSettings.openai_api_key.trim() } : {}),
    };
    aiSaveMutation.mutate(payload);
  };

  const handleAiUnsetKey = () => aiSaveMutation.mutate({ openai_api_key: '' });

  const handleColorChange = (e) => {
    const color = e.target.value;
    setFormData(prev => ({
      ...prev,
      company_clr: color
    }));
  };

  const validateImageFile = (file) => {
    const validTypes = ['image/png', 'image/jpeg', 'image/jpg', 'image/gif', 'image/webp'];
    if (!validTypes.includes(file.type)) {
      setError(t('settings.company.errors.invalidFileType'));
      return false;
    }
    const maxSize = 5 * 1024 * 1024;
    if (file.size > maxSize) {
      setError(t('settings.company.errors.fileTooLarge'));
      return false;
    }
    return true;
  };

  const handleLogoChange = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    if (!validateImageFile(file)) return;
    setLogoFile(file);
    setRemoveLogo(false);
    const reader = new FileReader();
    reader.onloadend = () => setLogoPreview(reader.result);
    reader.readAsDataURL(file);
  };

  const handleRemoveLogo = () => {
    setLogoFile(null);
    setLogoPreview(null);
    setRemoveLogo(true);
    setFormData(prev => ({ ...prev, company_logo: null }));
  };

  const handleLogoDarkChange = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    if (!validateImageFile(file)) return;
    setLogoDarkFile(file);
    setRemoveLogoDark(false);
    const reader = new FileReader();
    reader.onloadend = () => setLogoDarkPreview(reader.result);
    reader.readAsDataURL(file);
  };

  const handleRemoveLogoDark = () => {
    setLogoDarkFile(null);
    setLogoDarkPreview(null);
    setRemoveLogoDark(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    try {
      setSaving(true);
      setError(null);
      setSuccess(null);

      const submitData = new FormData();

      submitData.append('company_name', formData.company_name);
      submitData.append('company_clr', formData.company_clr);
      submitData.append('company_id', formData.company_id);
      submitData.append('tax_id', formData.tax_id);
      submitData.append('resp_person', formData.resp_person);
      submitData.append('street', formData.street);
      submitData.append('city', formData.city);
      submitData.append('postal_code', formData.postal_code);
      submitData.append('telephone', formData.telephone);
      submitData.append('mobile', formData.mobile);
      submitData.append('email', formData.email);
      submitData.append('description', formData.description);

      if (logoFile) {
        submitData.append('company_logo', logoFile);
      } else if (removeLogo) {
        submitData.append('company_logo', '');
      }
      if (logoDarkFile) {
        submitData.append('company_logo_dark', logoDarkFile);
      } else if (removeLogoDark) {
        submitData.append('company_logo_dark', '');
      }

      let response;
      if (company && company.id) {
        response = await api.put(`/companies/api/companies/${company.id}/`, submitData);
      } else {
        response = await api.post('/companies/api/companies/', submitData);
      }

      setCompany(response);
      setSuccess(t('settings.company.success.updated'));
      queryClient.invalidateQueries({ queryKey: ['company-settings'] });
    } catch (err) {
      console.error('Error saving company:', err);
      setError(t('settings.company.errors.saveFailed'));
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <PageShell pretitle={t('menu.settings')} title={t('settings.company.title')}>
        <CenteredLoader />
      </PageShell>
    );
  }

  return (
    <PageShell
      pretitle={t('menu.settings')}
      title={t('settings.company.title')}
    >
      {error && <div className="alert alert-danger app-inline-alert" role="alert">{error}</div>}
      {success && <div className="alert alert-success app-inline-alert" role="alert">{success}</div>}

      <Form id="companySettingsForm" onSubmit={handleSubmit}>
        <Row className="row-cards">
          {/* Branding column: logos + brand color stacked */}
          <Col lg={3}>
                {/* Light mode logo */}
            <PageSection title={t('settings.company.logoLight')} description={t('settings.company.logoLightSubtitle')} bodyClassName="text-center" className="mb-3">
              <div style={{ background: '#ffffff', borderRadius: '0 0 4px 4px', margin: '-1rem', padding: '1rem' }}>
                    {logoPreview ? (
                      <div className="mb-3">
                        <Image
                          src={logoPreview}
                          alt="Light Logo"
                          style={{ maxWidth: '100%', maxHeight: '160px', objectFit: 'contain' }}
                          className="mb-3"
                        />
                        <div className="d-grid gap-2">
                          <Button variant="outline-primary" size="sm" onClick={() => document.getElementById('logoUpload').click()}>
                            {t('settings.company.changeLogo')}
                          </Button>
                          <Button variant="outline-danger" size="sm" onClick={handleRemoveLogo}>
                            {t('settings.company.removeLogo')}
                          </Button>
                        </div>
                      </div>
                    ) : (
                      <div className="mb-3">
                        <div className="empty-icon mb-2">
                          <svg xmlns="http://www.w3.org/2000/svg" className="icon icon-lg" width="40" height="40" viewBox="0 0 24 24" strokeWidth="2" stroke="currentColor" fill="none" strokeLinecap="round" strokeLinejoin="round">
                            <path stroke="none" d="M0 0h24v24H0z" fill="none"/>
                            <path d="M15 8h.01"/>
                            <rect x="3" y="3" width="18" height="18" rx="2" ry="2"/>
                            <path d="M3 16l5 -5c.928 -.893 2.072 -.893 3 0l5 5"/>
                            <path d="M14 14l1 -1c.928 -.893 2.072 -.893 3 0l3 3"/>
                          </svg>
                        </div>
                        <Button variant="outline-primary" size="sm" onClick={() => document.getElementById('logoUpload').click()}>
                          {t('settings.company.uploadLogo')}
                        </Button>
                      </div>
                    )}
                    <input id="logoUpload" type="file" accept="image/png,image/jpeg,image/jpg,image/gif,image/webp" style={{ display: 'none' }} onChange={handleLogoChange} />
              </div>
            </PageSection>

            <PageSection title={t('settings.company.logoDark')} description={t('settings.company.logoDarkSubtitle')} bodyClassName="text-center" className="mb-3">
              <div style={{ background: '#1a1a2e', borderRadius: '0 0 4px 4px', margin: '-1rem', padding: '1rem' }}>
                    {logoDarkPreview ? (
                      <div className="mb-3">
                        <Image
                          src={logoDarkPreview}
                          alt="Dark Logo"
                          style={{ maxWidth: '100%', maxHeight: '160px', objectFit: 'contain' }}
                          className="mb-3"
                        />
                        <div className="d-grid gap-2">
                          <Button variant="outline-light" size="sm" onClick={() => document.getElementById('logoDarkUpload').click()}>
                            {t('settings.company.changeLogo')}
                          </Button>
                          <Button variant="outline-danger" size="sm" onClick={handleRemoveLogoDark}>
                            {t('settings.company.removeLogo')}
                          </Button>
                        </div>
                      </div>
                    ) : (
                      <div className="mb-3">
                        <div className="empty-icon mb-2">
                          <svg xmlns="http://www.w3.org/2000/svg" className="icon icon-lg text-white" width="40" height="40" viewBox="0 0 24 24" strokeWidth="2" stroke="white" fill="none" strokeLinecap="round" strokeLinejoin="round">
                            <path stroke="none" d="M0 0h24v24H0z" fill="none"/>
                            <path d="M15 8h.01"/>
                            <rect x="3" y="3" width="18" height="18" rx="2" ry="2"/>
                            <path d="M3 16l5 -5c.928 -.893 2.072 -.893 3 0l5 5"/>
                            <path d="M14 14l1 -1c.928 -.893 2.072 -.893 3 0l3 3"/>
                          </svg>
                        </div>
                        <Button variant="outline-light" size="sm" onClick={() => document.getElementById('logoDarkUpload').click()}>
                          {t('settings.company.uploadLogo')}
                        </Button>
                      </div>
                    )}
                    <input id="logoDarkUpload" type="file" accept="image/png,image/jpeg,image/jpg,image/gif,image/webp" style={{ display: 'none' }} onChange={handleLogoDarkChange} />
              </div>
            </PageSection>

            <PageSection title={t('settings.company.brandColor')}>
                    <div className="d-flex align-items-center gap-2">
                      <Form.Control
                        type="color"
                        name="company_clr"
                        value={formData.company_clr}
                        onChange={handleColorChange}
                        style={{ width: '60px', height: '40px' }}
                      />
                      <Form.Control
                        type="text"
                        value={formData.company_clr}
                        onChange={handleColorChange}
                        name="company_clr"
                        placeholder="#ffffff"
                      />
                    </div>
                    <Form.Text className="text-muted">
                      {t('settings.company.colorPickerLabel')}
                    </Form.Text>
            </PageSection>
          </Col>

          {/* Company Information */}
          <Col lg={9}>
            <PageSection title={t('settings.company.title')}>
                    <Row>
                      <Col md={12}>
                        <Form.Group className="mb-3">
                          <Form.Label>{t('settings.company.companyName')} *</Form.Label>
                          <Form.Control
                            type="text"
                            name="company_name"
                            value={formData.company_name}
                            onChange={handleInputChange}
                            placeholder={t('settings.company.placeholders.companyName')}
                            required
                          />
                        </Form.Group>
                      </Col>

                      <Col md={6}>
                        <Form.Group className="mb-3">
                          <Form.Label>{t('settings.company.companyId')}</Form.Label>
                          <Form.Control
                            type="text"
                            name="company_id"
                            value={formData.company_id}
                            onChange={handleInputChange}
                            placeholder={t('settings.company.placeholders.companyId')}
                          />
                        </Form.Group>
                      </Col>

                      <Col md={6}>
                        <Form.Group className="mb-3">
                          <Form.Label>{t('settings.company.taxId')}</Form.Label>
                          <Form.Control
                            type="text"
                            name="tax_id"
                            value={formData.tax_id}
                            onChange={handleInputChange}
                            placeholder={t('settings.company.placeholders.taxId')}
                          />
                        </Form.Group>
                      </Col>

                      <Col md={12}>
                        <Form.Group className="mb-3">
                          <Form.Label>{t('settings.company.responsiblePerson')}</Form.Label>
                          <Form.Control
                            type="text"
                            name="resp_person"
                            value={formData.resp_person}
                            onChange={handleInputChange}
                            placeholder={t('settings.company.placeholders.responsiblePerson')}
                          />
                        </Form.Group>
                      </Col>

                      <Col md={12}>
                        <Form.Group className="mb-3">
                          <Form.Label>{t('settings.company.street')}</Form.Label>
                          <Form.Control
                            type="text"
                            name="street"
                            value={formData.street}
                            onChange={handleInputChange}
                            placeholder={t('settings.company.placeholders.street')}
                          />
                        </Form.Group>
                      </Col>

                      <Col md={8}>
                        <Form.Group className="mb-3">
                          <Form.Label>{t('settings.company.city')}</Form.Label>
                          <Form.Control
                            type="text"
                            name="city"
                            value={formData.city}
                            onChange={handleInputChange}
                            placeholder={t('settings.company.placeholders.city')}
                          />
                        </Form.Group>
                      </Col>

                      <Col md={4}>
                        <Form.Group className="mb-3">
                          <Form.Label>{t('settings.company.postalCode')}</Form.Label>
                          <Form.Control
                            type="text"
                            name="postal_code"
                            value={formData.postal_code}
                            onChange={handleInputChange}
                            placeholder={t('settings.company.placeholders.postalCode')}
                          />
                        </Form.Group>
                      </Col>

                      <Col md={6}>
                        <Form.Group className="mb-3">
                          <Form.Label>{t('settings.company.telephone')}</Form.Label>
                          <Form.Control
                            type="text"
                            name="telephone"
                            value={formData.telephone}
                            onChange={handleInputChange}
                            placeholder={t('settings.company.placeholders.telephone')}
                          />
                        </Form.Group>
                      </Col>

                      <Col md={6}>
                        <Form.Group className="mb-3">
                          <Form.Label>{t('settings.company.mobile')}</Form.Label>
                          <Form.Control
                            type="text"
                            name="mobile"
                            value={formData.mobile}
                            onChange={handleInputChange}
                            placeholder={t('settings.company.placeholders.mobile')}
                          />
                        </Form.Group>
                      </Col>

                      <Col md={12}>
                        <Form.Group className="mb-3">
                          <Form.Label>{t('settings.company.email')}</Form.Label>
                          <Form.Control
                            type="email"
                            name="email"
                            value={formData.email}
                            onChange={handleInputChange}
                            placeholder={t('settings.company.placeholders.email')}
                          />
                        </Form.Group>
                      </Col>

                      <Col md={12}>
                        <Form.Group className="mb-3">
                          <Form.Label>{t('settings.company.description')}</Form.Label>
                          <Form.Control
                            as="textarea"
                            rows={3}
                            name="description"
                            value={formData.description}
                            onChange={handleInputChange}
                            placeholder={t('settings.company.placeholders.description')}
                          />
                        </Form.Group>
                      </Col>
                    </Row>
            </PageSection>
            <Row className="mt-3">
              <Col className="d-flex justify-content-end">
                <AppButton variant="primary" type="submit" form="companySettingsForm" loading={saving} loadingText={t('common.saving')}>
                  {t('common.save')}
                </AppButton>
              </Col>
            </Row>

          </Col>
        </Row>
      </Form>
    </PageShell>
  );
};

export default CompanySettings;
